import { BAND_COUNT, DREAD_REASONS, ENERGY_LEVELS, MOTIVATION_LEVELS, type Card, type CardEvent, type DreadReason, type Motivation } from '@hmawiw/shared';
import { isAlive } from '../store/cards';
import { DAY_MS, isLate, startOfDay } from './dates';

export type StatsRange = '4w' | '3m' | 'all';

export const AGE_BUCKETS = ['week', 'month', 'quarter', 'older'] as const;
export type AgeBucket = (typeof AGE_BUCKETS)[number];

export interface GroupStat {
  /** The column, band or energy level. */
  key: number;
  done: number;
  open: number;
  medianDays: number | null;
  p75Days: number | null;
}

export type ColumnStat = GroupStat & { motivation: Motivation };

/** The same numbers over the period before this one, when the period is bounded. */
export interface Trend {
  done: number;
  created: number;
  deleted: number;
  movesRight: number;
  easierShare: number | null;
}

export interface Stats {
  from: number;
  /** Any event at all: the log started with the version that added it. */
  hasHistory: boolean;
  doneCount: number;
  dread: {
    byColumn: ColumnStat[];
    byBand: GroupStat[];
    byEnergy: GroupStat[];
    /** Cards deleted rather than done, against the Dones of the period. */
    deleted: number;
    /** Cards that started in Dreading and were done: how many were done from a column to the right. */
    dreadedDoneRight: { moved: number; total: number };
    movesRight: number;
    movesLeft: number;
    forecast: { easier: number; worse: number };
  };
  levers: {
    /** The high-energy offer: shown, opened, and opened then done or touched within a day. */
    moment: { shown: number; opened: number; doneAfter: number };
    /** The morning pick: pinned, and done the same day. */
    morning: { pinned: number; doneSameDay: number };
    /** Cards by the reason last named, and how many of them moved right or were done afterwards. */
    dread: { reason: DreadReason; cards: number; movedOn: number }[];
  };
  lever: {
    ran: number;
    stopped: number;
    feel: { worse: number; same: number; better: number };
    /** Attempts followed within a week by a Done or a move to the right. */
    followed: { yes: number; total: number };
  };
  when: {
    /** Monday first. */
    byWeekday: number[];
    /** Six blocks of four hours from midnight. */
    byBlock: number[];
    energyAtDone: { below: number; atOrAbove: number; unset: number };
    /** [level 1, level 2, level 3], each by block. */
    energyByBlock: number[][];
  };
  keepingUp: {
    weeks: { start: number; done: number; created: number }[];
    /** Rows by age bucket, columns by motivation. */
    openAge: number[][];
    late: { late: number; dated: number };
    recurring: { onTime: number; total: number };
    ongoing: { within: number; gaps: number };
  };
  created: number;
  trend: { current: Trend; previous: Trend } | null;
}

const WEEK_MS = 7 * DAY_MS;

function rangeStart(range: StatsRange, now: number): number {
  if (range === '4w') return now - 4 * WEEK_MS;
  if (range === '3m') return now - 13 * WEEK_MS;
  return 0;
}

function quantile(sorted: number[], q: number): number | null {
  if (sorted.length === 0) return null;
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  const a = sorted[lo] as number;
  const b = sorted[hi] as number;
  return a + (b - a) * (pos - lo);
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** Monday-first weekday index. */
function weekday(ts: number): number {
  return (new Date(ts).getDay() + 6) % 7;
}

function block(ts: number): number {
  return Math.floor(new Date(ts).getHours() / 4);
}

/** Start of the Monday of the week holding `ts`. */
function weekStart(ts: number): number {
  const d = new Date(startOfDay(ts));
  d.setDate(d.getDate() - weekday(ts));
  return d.getTime();
}

function ageBucket(createdAt: number, now: number): number {
  const age = now - createdAt;
  if (age < WEEK_MS) return 0;
  if (age < 4 * WEEK_MS) return 1;
  if (age < 13 * WEEK_MS) return 2;
  return 3;
}

/** A Done as the statistics see it: from the log when there is one, else from the card itself. */
interface DoneRecord {
  cardId: string;
  at: number;
  motivation: Motivation;
  band: number;
  energy: number;
  myEnergy: number;
  days: number;
  dueAt: number | null;
  late: boolean;
}

function doneRecords(cards: Record<string, Card>, events: CardEvent[], from: number, to: number): DoneRecord[] {
  const out: DoneRecord[] = [];
  const logged = new Set<string>();
  // A Done later undone does not count; the last word on a card wins.
  const lastWord = new Map<string, 'done' | 'undone'>();
  for (const e of events) if (e.kind === 'done' || e.kind === 'undone') lastWord.set(e.cardId ?? '', e.kind);
  for (const e of events) {
    if (e.kind !== 'done' || e.cardId === null) continue;
    logged.add(e.cardId);
    if (e.at < from || e.at >= to || lastWord.get(e.cardId) === 'undone') continue;
    const createdAt = num(e.data['createdAt'], e.at);
    const dueAt = typeof e.data['dueAt'] === 'number' ? e.data['dueAt'] : null;
    out.push({
      cardId: e.cardId,
      at: e.at,
      motivation: num(e.data['motivation']) as Motivation,
      band: num(e.data['band'], 1),
      energy: num(e.data['energy'], 2),
      myEnergy: num(e.data['myEnergy']),
      days: (e.at - createdAt) / DAY_MS,
      dueAt,
      late: e.data['late'] === true,
    });
  }
  // Cards done before the log existed still tell how long they took.
  for (const c of Object.values(cards)) {
    if (!isAlive(c) || c.status !== 'done' || c.doneAt === null || logged.has(c.id) || c.doneAt < from || c.doneAt >= to) continue;
    out.push({ cardId: c.id, at: c.doneAt, motivation: c.motivation, band: c.band, energy: c.energy, myEnergy: 0, days: (c.doneAt - c.createdAt) / DAY_MS, dueAt: c.dueAt, late: c.dueAt !== null && isLate(c.dueAt, c.doneAt) });
  }
  return out.sort((a, b) => a.at - b.at);
}

/** Deletions that stood: a card restored afterwards was not thrown away. */
function deletedCount(cards: Record<string, Card>, events: CardEvent[], from: number, to: number): number {
  const lastWord = new Map<string, 'deleted' | 'restored'>();
  for (const e of events) if (e.kind === 'deleted' || e.kind === 'restored') lastWord.set(e.cardId ?? '', e.kind);
  const logged = new Set(events.filter((e) => e.kind === 'deleted').map((e) => e.cardId));
  let n = events.filter((e) => e.kind === 'deleted' && e.at >= from && e.at < to && lastWord.get(e.cardId ?? '') === 'deleted').length;
  // Deletions from before the log: the tombstone itself.
  for (const c of Object.values(cards)) if (c.deletedAt !== null && !logged.has(c.id) && c.deletedAt >= from && c.deletedAt < to) n++;
  return n;
}

function trendOf(cards: Record<string, Card>, events: CardEvent[], from: number, to: number): Trend {
  const dones = doneRecords(cards, events, from, to);
  const window = events.filter((e) => e.at >= from && e.at < to);
  const moves = window.filter((e) => e.kind === 'moved' && e.data['cause'] !== 'undo' && e.data['cause'] !== 'cancel');
  const easier = window.filter((e) => e.kind === 'forecast' && e.data['answer'] === 'easier').length;
  const worse = window.filter((e) => e.kind === 'forecast' && e.data['answer'] === 'worse').length;
  return {
    done: dones.length,
    created: Object.values(cards).filter((c) => c.createdAt >= from && c.createdAt < to).length,
    deleted: deletedCount(cards, events, from, to),
    movesRight: moves.filter((e) => num(e.data['motivation']) > num(e.data['from'])).length,
    easierShare: easier + worse === 0 ? null : easier / (easier + worse),
  };
}

function groupStats<K extends number>(keys: K[], dones: DoneRecord[], open: Card[], pick: (d: DoneRecord | Card) => number): (GroupStat & { key: K })[] {
  return keys.map((key) => {
    const days = dones.filter((d) => pick(d) === key).map((d) => d.days).sort((a, b) => a - b);
    return { key, done: days.length, open: open.filter((c) => pick(c) === key).length, medianDays: quantile(days, 0.5), p75Days: quantile(days, 0.75) };
  });
}

export function statsOf(cards: Record<string, Card>, events: CardEvent[], now: number, range: StatsRange, columnCount = MOTIVATION_LEVELS): Stats {
  const from = rangeStart(range, now);
  const to = now + 1;
  const inRange = events.filter((e) => e.at >= from);
  const dones = doneRecords(cards, events, from, to);
  const open = Object.values(cards).filter((c) => isAlive(c) && c.status === 'open');
  const columns = Array.from({ length: columnCount }, (_, i) => i as Motivation);

  const byColumn: ColumnStat[] = groupStats(columns, dones, open, (x) => x.motivation).map((g) => ({ ...g, motivation: g.key }));
  const byBand = groupStats(Array.from({ length: BAND_COUNT }, (_, i) => i), dones, open, (x) => x.band);
  const byEnergy = groupStats(Array.from({ length: ENERGY_LEVELS }, (_, i) => i + 1), dones, open, (x) => x.energy);
  const createdDreading = new Set(events.filter((e) => e.kind === 'created' && num(e.data['motivation']) === 0).map((e) => e.cardId));
  const dreadedDone = dones.filter((d) => createdDreading.has(d.cardId));
  const moves = inRange.filter((e) => e.kind === 'moved' && e.data['cause'] !== 'undo' && e.data['cause'] !== 'cancel');
  const dread = {
    byColumn,
    byBand,
    byEnergy,
    deleted: deletedCount(cards, events, from, to),
    dreadedDoneRight: { moved: dreadedDone.filter((d) => d.motivation > 0).length, total: dreadedDone.length },
    movesRight: moves.filter((e) => num(e.data['motivation']) > num(e.data['from'])).length,
    movesLeft: moves.filter((e) => num(e.data['motivation']) < num(e.data['from'])).length,
    forecast: {
      easier: inRange.filter((e) => e.kind === 'forecast' && e.data['answer'] === 'easier').length,
      worse: inRange.filter((e) => e.kind === 'forecast' && e.data['answer'] === 'worse').length,
    },
  };

  const attempts = inRange.filter((e) => e.kind === 'attempt');
  const feels = inRange.filter((e) => e.kind === 'feel');
  const followed = attempts.filter((a) =>
    events.some((e) => e.cardId === a.cardId && e.id !== a.id && e.at >= a.at && e.at <= a.at + WEEK_MS && (e.kind === 'done' || (e.kind === 'moved' && num(e.data['motivation']) > num(e.data['from']) && e.data['cause'] !== 'undo'))),
  );
  const lever = {
    ran: attempts.filter((e) => e.data['result'] === 'ran').length,
    stopped: attempts.filter((e) => e.data['result'] === 'stopped').length,
    feel: {
      worse: feels.filter((e) => num(e.data['answer']) < 0).length,
      same: feels.filter((e) => num(e.data['answer']) === 0).length,
      better: feels.filter((e) => num(e.data['answer']) > 0).length,
    },
    followed: { yes: followed.length, total: attempts.length },
  };

  const offers = inRange.filter((e) => e.kind === 'offer');
  const progressAfter = (cardId: string | null, from: number, until: number): boolean =>
    cardId !== null &&
    events.some((e) => e.cardId === cardId && e.at >= from && e.at <= until && (e.kind === 'done' || e.kind === 'touched' || (e.kind === 'moved' && num(e.data['motivation']) > num(e.data['from']) && e.data['cause'] !== 'undo')));
  const momentOffers = offers.filter((e) => e.data['kind'] === 'moment');
  const opened = momentOffers.filter((e) => e.data['answer'] === 'open');
  const pinned = offers.filter((e) => e.data['kind'] === 'morning' && e.data['answer'] === 'pinned');
  const endOfDay = (ts: number): number => startOfDay(ts) + DAY_MS - 1;
  const lastReason = new Map<string, DreadReason>();
  for (const e of events) if (e.kind === 'dread' && e.cardId && e.at >= from) lastReason.set(e.cardId, e.data['reason'] as DreadReason);
  const dreadAt = new Map<string, number>();
  for (const e of events) if (e.kind === 'dread' && e.cardId) dreadAt.set(e.cardId, e.at);
  const levers = {
    moment: {
      shown: momentOffers.filter((e) => e.data['answer'] === 'shown').length,
      opened: opened.length,
      doneAfter: opened.filter((e) => progressAfter(e.cardId, e.at, e.at + DAY_MS)).length,
    },
    morning: {
      pinned: pinned.length,
      doneSameDay: pinned.filter((e) => events.some((d) => d.cardId === e.cardId && (d.kind === 'done' || d.kind === 'touched') && d.at >= e.at && d.at <= endOfDay(e.at))).length,
    },
    dread: DREAD_REASONS.map((reason) => {
      const ids = [...lastReason.entries()].filter(([, r]) => r === reason).map(([id]) => id);
      return { reason, cards: ids.length, movedOn: ids.filter((id) => progressAfter(id, dreadAt.get(id) ?? 0, Number.MAX_SAFE_INTEGER)).length };
    }),
  };

  const byWeekday = Array.from({ length: 7 }, () => 0);
  const byBlock = Array.from({ length: 6 }, () => 0);
  const energyAtDone = { below: 0, atOrAbove: 0, unset: 0 };
  for (const d of dones) {
    byWeekday[weekday(d.at)] = (byWeekday[weekday(d.at)] ?? 0) + 1;
    byBlock[block(d.at)] = (byBlock[block(d.at)] ?? 0) + 1;
    if (d.myEnergy === 0) energyAtDone.unset++;
    else if (d.myEnergy < d.energy) energyAtDone.below++;
    else energyAtDone.atOrAbove++;
  }
  const energyByBlock = [1, 2, 3].map(() => Array.from({ length: 6 }, () => 0));
  for (const e of inRange) {
    if (e.kind !== 'energy') continue;
    const level = num(e.data['level']);
    const row = energyByBlock[level - 1];
    if (row) row[block(e.at)] = (row[block(e.at)] ?? 0) + 1;
  }
  const when = { byWeekday, byBlock, energyAtDone, energyByBlock };

  const weekCount = range === '4w' ? 4 : range === '3m' ? 13 : Math.min(26, Math.max(4, Math.ceil((now - Math.min(...Object.values(cards).map((c) => c.createdAt), now)) / WEEK_MS) + 1));
  const thisWeek = weekStart(now);
  const weeks = Array.from({ length: weekCount }, (_, i) => ({ start: thisWeek - (weekCount - 1 - i) * WEEK_MS, done: 0, created: 0 }));
  const weekIndex = (ts: number): number => weekCount - 1 - Math.floor((thisWeek - weekStart(ts)) / WEEK_MS + 0.5);
  for (const d of dones) {
    const i = weekIndex(d.at);
    if (i >= 0 && i < weekCount) (weeks[i] as { done: number }).done++;
  }
  for (const c of Object.values(cards)) {
    if (c.createdAt < from) continue;
    const i = weekIndex(c.createdAt);
    if (i >= 0 && i < weekCount) (weeks[i] as { created: number }).created++;
  }
  const openAge = AGE_BUCKETS.map(() => columns.map(() => 0));
  for (const c of open) {
    const row = openAge[ageBucket(c.createdAt, now)] as number[];
    row[c.motivation] = (row[c.motivation] ?? 0) + 1;
  }
  const dated = dones.filter((d) => d.dueAt !== null);
  const touches = inRange.filter((e) => e.kind === 'touched');
  const recurringTouches = touches.filter((e) => e.data['ongoing'] !== true);
  let within = 0;
  let gaps = 0;
  for (const c of open) {
    if (c.repeat?.kind !== 'ongoing') continue;
    const times = touches.filter((e) => e.cardId === c.id).map((e) => e.at);
    let previous = c.createdAt;
    for (const t of times) {
      gaps++;
      if (t - previous <= c.repeat.days * DAY_MS) within++;
      previous = t;
    }
  }
  const keepingUp = {
    weeks,
    openAge,
    late: { late: dated.filter((d) => d.late).length, dated: dated.length },
    recurring: { onTime: recurringTouches.filter((e) => e.data['late'] !== true).length, total: recurringTouches.length },
    ongoing: { within, gaps },
  };

  const created = Object.values(cards).filter((c) => c.createdAt >= from).length;
  const trend = range === 'all' ? null : { current: trendOf(cards, events, from, to), previous: trendOf(cards, events, from - (now - from), from) };
  return { from, hasHistory: events.length > 0, doneCount: dones.length, dread, levers, lever, when, keepingUp, created, trend };
}
